import { v4 as uuidv4 } from "uuid";
import { termsCoEfficientTable } from "../../constants";
import { portsByChar } from "../../containers/User/Calculator/actions";

const sessionPortCache = new Map();

export const resolveAIPorts = async (uniquePortNames) => {
  const resolvedPortsMap = {};

  const resolvePromises = uniquePortNames.map(async (portName) => {
    if (!portName) return;

    // Check in-memory cache first
    if (sessionPortCache.has(portName.toLowerCase())) {
      resolvedPortsMap[portName] = sessionPortCache.get(portName.toLowerCase());
      return;
    }

    try {
      const res = await portsByChar(portName);
      const portList = res?.data?.list || [];

      if (portList.length === 1) {
        // High confidence: Exactly one match
        const matched = portList[0];
        const resolvedData = { port: matched.name, lat: matched.lat, lng: matched.lng, resolved: true };
        resolvedPortsMap[portName] = resolvedData;
        sessionPortCache.set(portName.toLowerCase(), resolvedData);
      } else if (portList.length > 1) {
        // Evaluate multiple matches
        const exactMatch = portList.find(p => p.name.toLowerCase() === portName.toLowerCase());

        // Ensure there's only one exact match (avoids Newcastle AU vs UK issue if multiple exact matches)
        const exactMatchesCount = portList.filter(p => p.name.toLowerCase() === portName.toLowerCase()).length;

        if (exactMatch && exactMatchesCount === 1) {
          // High confidence: One exact string match
          const resolvedData = { port: exactMatch.name, lat: exactMatch.lat, lng: exactMatch.lng, resolved: true };
          resolvedPortsMap[portName] = resolvedData;
          sessionPortCache.set(portName.toLowerCase(), resolvedData);
        } else {
          // Low confidence: Multiple fuzzy matches, or multiple exact matches (duplicates across countries)
          resolvedPortsMap[portName] = { port: portName, lat: null, lng: null, resolved: false };
        }
      } else {
        // Low confidence: No results
        resolvedPortsMap[portName] = { port: portName, lat: null, lng: null, resolved: false };
      }
    } catch (error) {
      console.error(`Failed to resolve port: ${portName}`, error);
      resolvedPortsMap[portName] = { port: portName, lat: null, lng: null, resolved: false };
    }
  });

  await Promise.allSettled(resolvePromises);
  return resolvedPortsMap;
};

export const mapValidCargoToState = (validCargo, currentQuery, currentPortOperations, addPortInitialData, resolvedPortsMap = {}) => {
  const nextQuery = { ...currentQuery };
  let nextPortOperations = [...currentPortOperations];

  // Map query fields
  if (validCargo.name !== undefined) nextQuery.name = validCargo.name;
  if (validCargo.message !== undefined) nextQuery.message = validCargo.message;
  if (validCargo.freightMode !== undefined) nextQuery.freightMode = validCargo.freightMode;
  if (validCargo.qtyMode !== undefined) nextQuery.qtyMode = validCargo.qtyMode;
  if (validCargo.suez !== undefined) nextQuery.suez = validCargo.suez;
  if (validCargo.panama !== undefined) nextQuery.panama = validCargo.panama;
  if (validCargo.laycanStartDate !== undefined) nextQuery.laycanStartDate = validCargo.laycanStartDate;
  if (validCargo.laycanEndDate !== undefined) nextQuery.laycanEndDate = validCargo.laycanEndDate;

  // Map port operations
  if (validCargo.portOperations && validCargo.portOperations.length > 0) {
    nextPortOperations = validCargo.portOperations.map((aiOp, index) => {
      const template = index === 0 ? addPortInitialData[0] : addPortInitialData[1];
      const baseOp = { ...template, _id: uuidv4() };

      const mappedOp = { ...baseOp, ...aiOp };
      const termFactor = termsCoEfficientTable.find((t) => t.label === mappedOp.terms);
      if (termFactor) mappedOp.factor = termFactor.value;

      // Apply resolved port data if available
      if (mappedOp.port && resolvedPortsMap[mappedOp.port]) {
        const resolvedInfo = resolvedPortsMap[mappedOp.port];
        mappedOp.port = resolvedInfo.port;
        mappedOp.lat = resolvedInfo.lat;
        mappedOp.lng = resolvedInfo.lng;
        // We set a flag so UI knows it needs validation if unresolved
        if (!resolvedInfo.resolved) {
          mappedOp.needsValidation = true;
        }
      }

      return mappedOp;
    });

    if (nextPortOperations.length === 1) {
      nextPortOperations.push({ ...addPortInitialData[1], _id: uuidv4() });
    }
  }

  return { nextQuery, nextPortOperations };
};

const op = (operation) => ({ operation, port: "", cargoQty: 0, tolerance: 0, rate: 0, terms: "SHINC", turnTime: 0, pdaRemarks: "", grade: "", freight: 0, adcom: 0, other: 0, brokerage: 0, ttlComm: 0, netFrt: 0, min: 0, max: 0, account: "", cargo: "", factor: 1, stowFactor: 0, queryId: "", loadline: "S-Summer", sulfur: "Normal" });
export const ADD_PORT_INITIAL_DATA = [op("Loading"), op("Discharging")];

export const applyValidCargo = async (validCargo, { query, portOperations, setQuery, setPortOperations, onStatus = () => { } }) => {
  onStatus("Resolving Port Locations...");
  const uniquePorts = Array.from(new Set((validCargo.portOperations || []).map((o) => o.port).filter(Boolean)));
  const resolvedPortsMap = await resolveAIPorts(uniquePorts);
  const { nextQuery, nextPortOperations } = mapValidCargoToState(
    validCargo, query || {}, portOperations || ADD_PORT_INITIAL_DATA, ADD_PORT_INITIAL_DATA, resolvedPortsMap
  );
  setQuery(nextQuery);
  setPortOperations(nextPortOperations);
  return nextPortOperations.filter((o) => o.needsValidation).length;
};
